/**
 * Reviews API hooks — TanStack Query layer over /api/reviews.
 *
 * Covers review cycles, reviews (with share/unshare), and 360 feedback.
 *
 * Visibility rules (enforced in the handler):
 *   - Employees only see their own reviews that have been Shared
 *   - Admins see all reviews except those about themselves (separation of duties)
 *   - Managers see reviews for their direct reports
 *
 * Note: the server's review list query supports `employeeId`; `cycleId` is carried
 * in the query key for cache isolation but is not a server-side filter (filter
 * client-side on the returned `items` when needed).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../../lib/api.js';
import type { Review, ReviewCycle, ReviewFeedback, Paginated } from '@shared/types';
import {
  reviewCycleCreateInput,
  reviewCreateInput,
  reviewUpdateInput,
  reviewFeedbackCreateInput,
} from '@shared/schemas/index.js';
import { z } from 'zod';

export type ReviewCycleCreateBody = z.input<typeof reviewCycleCreateInput>;
export type ReviewCreateBody = z.input<typeof reviewCreateInput>;
export type ReviewUpdateBody = z.input<typeof reviewUpdateInput>;
export type ReviewFeedbackCreateBody = z.input<typeof reviewFeedbackCreateInput>;

export const reviewKeys = {
  all: ['reviews'] as const,
  cycles: () => [...reviewKeys.all, 'cycles'] as const,
  list: (params?: { employeeId?: string; cycleId?: string }) =>
    [...reviewKeys.all, 'list', params ?? {}] as const,
  detail: (id: string) => [...reviewKeys.all, 'detail', id] as const,
  feedback: (reviewId: string) =>
    [...reviewKeys.all, 'feedback', reviewId] as const,
};

// ── Review cycles ─────────────────────────────────────────────────

/** List all review cycles. */
export function useReviewCycles() {
  return useQuery({
    queryKey: reviewKeys.cycles(),
    queryFn: () => api.get<Paginated<ReviewCycle>>('/api/reviews/cycles'),
  });
}

/** Create a new review cycle (Admin/Manager). */
export function useCreateCycle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ReviewCycleCreateBody) =>
      api.post<ReviewCycle>('/api/reviews/cycles', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reviewKeys.cycles() });
    },
  });
}

// ── Reviews ───────────────────────────────────────────────────────

/** List reviews, optionally filtered by employee. */
export function useReviews(params?: { employeeId?: string; cycleId?: string }) {
  return useQuery({
    queryKey: reviewKeys.list(params),
    queryFn: () =>
      api.get<Paginated<Review>>(`/api/reviews${qs({ employeeId: params?.employeeId })}`),
  });
}

/** Fetch a single review by id. Disabled until `id` is provided. */
export function useReview(id: string | undefined) {
  return useQuery({
    queryKey: reviewKeys.detail(id ?? ''),
    queryFn: () => api.get<Review>(`/api/reviews/${id}`),
    enabled: Boolean(id),
  });
}

/** Create a review (Admin/Manager; cannot be about self). */
export function useCreateReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ReviewCreateBody) =>
      api.post<Review>('/api/reviews', body),
    onSuccess: (created) => {
      queryClient.setQueryData(reviewKeys.detail(created.id), created);
      void queryClient.invalidateQueries({ queryKey: reviewKeys.all });
    },
  });
}

/** Update review content (Admin/Manager). */
export function useUpdateReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ReviewUpdateBody }) =>
      api.put<Review>(`/api/reviews/${id}`, body),
    onSuccess: (updated) => {
      queryClient.setQueryData(reviewKeys.detail(updated.id), updated);
      void queryClient.invalidateQueries({ queryKey: reviewKeys.all });
    },
  });
}

/** Share a review with its subject employee (Admin/Manager). */
export function useShareReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<Review>(`/api/reviews/${id}/share`),
    onSuccess: (updated) => {
      queryClient.setQueryData(reviewKeys.detail(updated.id), updated);
      void queryClient.invalidateQueries({ queryKey: reviewKeys.all });
    },
  });
}

/** Unshare a review (revert to Draft) (Admin/Manager). */
export function useUnshareReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<Review>(`/api/reviews/${id}/unshare`),
    onSuccess: (updated) => {
      queryClient.setQueryData(reviewKeys.detail(updated.id), updated);
      void queryClient.invalidateQueries({ queryKey: reviewKeys.all });
    },
  });
}

// ── 360 feedback ──────────────────────────────────────────────────

/** List feedback entries for a review. */
export function useReviewFeedback(reviewId: string | undefined) {
  return useQuery({
    queryKey: reviewKeys.feedback(reviewId ?? ''),
    queryFn: () =>
      api.get<{ items: ReviewFeedback[] }>(`/api/reviews/${reviewId}/feedback`),
    enabled: Boolean(reviewId),
  });
}

/** Add feedback to a review. */
export function useCreateFeedback(reviewId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ReviewFeedbackCreateBody) =>
      api.post<ReviewFeedback>(`/api/reviews/${reviewId}/feedback`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: reviewKeys.feedback(reviewId),
      });
    },
  });
}

/** Delete a review (Admin/Manager; author-scoped for managers). */
export function useDeleteReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: boolean }>(`/api/reviews/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reviewKeys.all });
    },
  });
}
